import React from "react";
import moment from "moment";
import { Modal, FormInput, FormSelect, Button, Loader } from "ui-kit-ck-consultant";

import axiosConfig from "../../../utils/axiosConfig";

const ERRORS = {
  E_SLOT_TAKEN: "Ce créneau vient d'être pris.",
  E_SLOT_OUTSIDE_HOURS: "Ce créneau est en dehors des horaires de l'agence.",
  E_FOLDER_UNKNOWN: "Ce dossier est introuvable.",
  E_AGENCY_NOT_ALLOWED: "Cette agence n'est pas dans votre périmètre.",
  E_IXPERIENCE_UNREACHABLE: "ixperience ne répond pas : le rendez-vous n'a pas été pris.",
  E_IXPERIENCE_UNAUTHORIZED: "ixperience a refusé la demande (secret partagé).",
  E_IXPERIENCE_SECRET_MISSING: "Le secret partagé avec ixperience n'est pas configuré.",
  E_VALIDATION: "Les informations envoyées sont incomplètes.",
};

const message = (code) => ERRORS[code] || "Une erreur est survenue, le rendez-vous n'a pas été pris.";

const folderLabel = (folder) =>
  [
    folder.reference,
    [folder.kindClient, folder.nameClient].filter(Boolean).join(" "),
    folder.licensePlate,
    folder.agencyName,
  ]
    .filter(Boolean)
    .join(" — ");

const normalize = (text) =>
  (text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/**
 * Gives a slot of the agency to a folder.
 *
 * The folder comes first, the slot second: a cell clicked on the week only
 * pre-fills the date and the hour, all stay editable. A chosen folder brings
 * its own agency, which another one of the list can replace. Moving an
 * existing appointment is the same form with the folder already chosen and
 * the slot's agency kept: booking replaces the previous slot.
 *
 * Saving does exactly what the insured's own booking does, through ixperience:
 * the appointment is recorded, the date pushed to ALPHA and the RETOUR-DATE
 * document filed. The hour never reaches ALPHA — it lives in that document and
 * in the agenda.
 */
export default class ModalBookSlot extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      search: "",
      searched: null,
      folders: [],
      selected: props.folder || null,
      agency: { id: props.agencyId, name: props.agencyName },
      agencyFilter: "",
      isConfirming: false,
      date: props.date || moment().format("YYYY-MM-DD"),
      hour: props.hour || "",
      hours: [],
      isSearching: false,
      isHoursLoading: false,
      isLoading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.loadHours();
  }

  // The hours the agency still has free that day, from the same source the
  // week is drawn from.
  loadHours = (then) => {
    const { date, agency } = this.state;
    if (!moment(date, "YYYY-MM-DD", true).isValid()) {
      this.setState({ hours: [], hour: "" });
      return;
    }
    this.setState({ isHoursLoading: true }, () =>
      axiosConfig("planning/appointment/get-week", {
        data: {
          agencyId: agency.id,
          startDate: date,
          endDate: date,
          folderId: this.state.selected ? this.state.selected.folderId : null,
        },
      })
        .then((res) => {
          if (!res.data.success) {
            this.setState({ isHoursLoading: false, hours: [], error: message(res.data.code) });
            return;
          }
          const day = res.data.data.days[0];
          const hours = day.open ? day.free : [];
          this.setState(
            (state) => ({
              isHoursLoading: false,
              hours,
              hour: hours.includes(state.hour) ? state.hour : "",
              error: null,
            }),
            typeof then === "function" ? then : undefined
          );
        })
        .catch(() => this.setState({ isHoursLoading: false, hours: [], error: message() }))
    );
  };

  handleSearch = () => {
    const search = this.state.search.trim();
    if (search.length < 2) {
      this.setState({ error: "Saisissez au moins deux caractères." });
      return;
    }
    this.setState({ isSearching: true, error: null }, () =>
      axiosConfig("planning/appointment/search-folders", {
        data: { search, region: this.props.region, agency: this.props.agencyFilters },
      })
        .then((res) =>
          this.setState({
            isSearching: false,
            folders: res.data.success ? res.data.data : [],
            searched: res.data.success ? search : null,
            error: res.data.success ? null : message(res.data.code),
          })
        )
        .catch(() => this.setState({ isSearching: false, error: message() }))
    );
  };

  handleSelectFolder = (folder) =>
    this.setState(
      (state) => ({
        selected: folder,
        agency: folder.agencyId ? { id: folder.agencyId, name: folder.agencyName } : state.agency,
      }),
      this.loadHours
    );

  handleAgencyChange = (event) => {
    const agency = this.agencyOptions().find((item) => String(item.id) === event.target.value);
    if (!agency) return;
    this.setState({ agency: { id: agency.id, name: agency.name }, isConfirming: false }, this.loadHours);
  };

  // The search bar's agencies, plus the folder's and the one chosen when the
  // filters leave them out.
  agencyOptions = () => {
    const { selected, agency } = this.state;
    const options = [...(this.props.agencies || [])];
    const add = (id, name) => {
      if (id && !options.some((item) => Number(item.id) === Number(id))) options.push({ id, name });
    };
    add(agency.id, agency.name);
    if (selected) add(selected.agencyId, selected.agencyName);
    return options;
  };

  isOtherAgency = () => {
    const { selected, agency } = this.state;
    return !!(selected && selected.agencyId && Number(selected.agencyId) !== Number(agency.id));
  };

  handleSave = () => {
    if (this.isOtherAgency()) this.setState({ isConfirming: true });
    else this.handleBook();
  };

  // Same date and hour, at the folder's agency: its hours and bookings differ,
  // so the slot is checked there before booking.
  handleBookAtFolderAgency = () => {
    const { selected, hour } = this.state;
    this.setState(
      { agency: { id: selected.agencyId, name: selected.agencyName }, isConfirming: false },
      () =>
        this.loadHours(() => {
          if (this.state.hour === hour) this.handleBook();
          else
            this.setState({
              error: `Le créneau de ${hour} n'est pas libre à ${selected.agencyName} ce jour : choisissez une autre heure.`,
            });
        })
    );
  };

  handleBook = () => {
    const { selected, date, hour, agency } = this.state;
    if (!selected || !hour) return;

    this.setState({ isLoading: true, isConfirming: false, error: null }, () =>
      axiosConfig("planning/appointment/book", {
        data: { agencyId: agency.id, folderId: selected.folderId, date, hour },
      })
        .then((res) => {
          if (res.data.success) {
            this.setState({ isLoading: false }, () => this.props.onBooked(agency.id));
          } else {
            this.setState({ isLoading: false, error: message(res.data.code) });
            // The slot may have gone while the form was open: re-read the day.
            if (res.data.code === "E_SLOT_TAKEN") this.loadHours();
          }
        })
        .catch(() => this.setState({ isLoading: false, error: message() }))
    );
  };

  renderFolderStep() {
    const { selected, folders, isSearching } = this.state;

    if (selected) {
      return (
        <div className="AppointmentGrid-folder AppointmentGrid-folder-selected">
          <b>{folderLabel(selected)}</b>
          {selected.appointmentDate ? (
            <span className="AppointmentGrid-folder-warning">
              {" "}
              (déjà prévu le {moment(selected.appointmentDate).format("DD/MM/YYYY")} à{" "}
              {selected.appointmentHour} — ce rendez-vous le remplacera)
            </span>
          ) : null}
          {this.props.folder ? null : (
            <Button
              className="w-auto ml-10"
              text="Changer"
              outline
              small
              onClick={() =>
                this.setState(
                  {
                    selected: null,
                    isConfirming: false,
                    agency: { id: this.props.agencyId, name: this.props.agencyName },
                    agencyFilter: "",
                  },
                  this.loadHours
                )
              }
            />
          )}
        </div>
      );
    }

    return (
      <>
        <div className="AppointmentGrid-search">
          <FormInput
            className="AppointmentGrid-search-input"
            title="Dossier"
            placeholder="Référence, immatriculation, nom ou sinistre — toutes agences"
            value={this.state.search}
            onChange={(event) => this.setState({ search: event.target.value, searched: null })}
            onKeyPress={(event) => (event.key === "Enter" ? this.handleSearch() : null)}
          />
          <Button className="w-auto" text="Rechercher" disabled={isSearching} onClick={this.handleSearch} />
        </div>

        {isSearching ? <Loader /> : null}

        {!isSearching && folders.length ? (
          <div className="AppointmentGrid-results mt-10">
            {folders.map((folder) => (
              <div
                key={folder.folderId}
                role="button"
                tabIndex={0}
                className="AppointmentGrid-folder"
                onClick={() => this.handleSelectFolder(folder)}
                onKeyPress={() => this.handleSelectFolder(folder)}
              >
                <b>{folder.reference}</b> — {[folder.kindClient, folder.nameClient].filter(Boolean).join(" ")}
                {folder.licensePlate ? ` — ${folder.licensePlate}` : ""}
                {folder.agencyName ? ` — ${folder.agencyName}` : ""}
                {folder.expert ? ` (${folder.expert})` : ""}
                {folder.appointmentDate ? (
                  <span className="AppointmentGrid-folder-warning">
                    {" "}
                    (déjà prévu le {moment(folder.appointmentDate).format("DD/MM/YYYY")} à{" "}
                    {folder.appointmentHour})
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {!isSearching && !folders.length && this.state.searched ? (
          <p className="mt-10 mb-0">Aucun dossier ouvert ne correspond à « {this.state.searched} ».</p>
        ) : null}
      </>
    );
  }

  renderAgencyStep() {
    const { agency, agencyFilter } = this.state;
    const filter = normalize(agencyFilter);
    const options = this.agencyOptions().filter(
      (item) => Number(item.id) === Number(agency.id) || normalize(item.name).includes(filter)
    );
    return (
      <div className="AppointmentGrid-slot mt-20">
        <FormInput
          className="AppointmentGrid-field"
          title="Rechercher une agence"
          placeholder="Nom de l'agence"
          value={agencyFilter}
          onChange={(event) => this.setState({ agencyFilter: event.target.value })}
        />
        <FormSelect
          className="AppointmentGrid-field"
          title="Agence du rendez-vous"
          ignore={true}
          value={agency.id ? String(agency.id) : "DEFAULT"}
          options={options.map((item) => ({ value: String(item.id), text: item.name }))}
          onChange={this.handleAgencyChange}
        />
      </div>
    );
  }

  renderSlotStep() {
    const { date, hour, hours, isHoursLoading } = this.state;
    return (
      <div className="AppointmentGrid-slot mt-20">
        <FormInput
          className="AppointmentGrid-slot-date"
          title="Date"
          type="date"
          value={date}
          onChange={(event) => this.setState({ date: event.target.value, isConfirming: false }, this.loadHours)}
        />
        <FormSelect
          className="AppointmentGrid-slot-hour"
          title="Heure"
          ignore={true}
          init={isHoursLoading ? "Chargement…" : hours.length ? "Choisir une heure" : "Aucun créneau libre ce jour"}
          value={hour || "DEFAULT"}
          disabled={isHoursLoading || !hours.length}
          options={hours.map((slot) => ({ value: slot, text: slot }))}
          onChange={(event) =>
            this.setState({ hour: event.target.value === "DEFAULT" ? "" : event.target.value, isConfirming: false })
          }
        />
      </div>
    );
  }

  renderConfirm() {
    const { selected, agency } = this.state;
    return (
      <>
        <p className="AppointmentGrid-folder-warning mt-20 mb-0">
          Vous avez choisi de fixer le rendez-vous sur l'agence <b>{agency.name}</b>, mais le dossier{" "}
          <b>{selected.reference}</b> est actuellement sur l'agence <b>{selected.agencyName}</b>. Êtes-vous
          sûr ?
        </p>
        <div className="AppointmentGrid-footer AppointmentGrid-actions">
          <Button className="w-auto" text="Retour" outline onClick={() => this.setState({ isConfirming: false })} />
          <Button className="w-auto" text={`Oui, sur ${agency.name}`} green onClick={this.handleBook} />
          <Button
            className="w-auto"
            text={`Non, sur ${selected.agencyName}`}
            onClick={this.handleBookAtFolderAgency}
          />
        </div>
      </>
    );
  }

  render() {
    const { show, onClose, folder } = this.props;
    const { selected, hour, agency, isConfirming, isLoading, error } = this.state;
    const title = folder ? "Déplacer le rendez-vous" : "Rendez-vous";

    return (
      <Modal title={`${title}${agency.name ? ` — ${agency.name}` : ""}`} show={show} onClose={onClose}>
        {this.renderFolderStep()}
        {selected ? this.renderAgencyStep() : null}
        {selected ? this.renderSlotStep() : null}

        {error ? <p className="red mt-10 mb-0">{error}</p> : null}

        {isConfirming ? (
          this.renderConfirm()
        ) : (
          <div className="AppointmentGrid-footer">
            <Button
              className="w-auto"
              text={isLoading ? "Enregistrement…" : folder ? "Déplacer le rendez-vous" : "Enregistrer le rendez-vous"}
              green
              disabled={!selected || !hour || isLoading}
              onClick={this.handleSave}
            />
          </div>
        )}
      </Modal>
    );
  }
}
